import { useAuth0 } from "@auth0/auth0-react";
import { Box, Button, CircularProgress, Typography, Alert, IconButton } from "@mui/material";
import { LogoutOutlined } from "@mui/icons-material";
import React, { useEffect } from "react";
import { sec } from "../utils/security";
import { useApiConfiguration } from "../hooks/useApiConfiguration";

type AuthGateProps = {
  children: React.ReactNode;
};

const AuthGate = ({ children }: AuthGateProps) => {
  const { isAuthenticated, isLoading, loginWithPopup, getAccessTokenSilently, user, logout } = useAuth0();
  const { 
    isLoading: isConfiguringApi, 
    isConfigured: isApiConfigured, 
    error: apiConfigError,
    refreshConfiguration 
  } = useApiConfiguration();

  // Initialize security functions
  useEffect(() => {
    sec.setAccessTokenSilently(getAccessTokenSilently);
    sec.setGetUser(() => user);
  }, [getAccessTokenSilently, user]);

  // Reusable logout button component
  const LogoutButton = () => (
    <IconButton
      title="Log Out"
      color="inherit"
      size="small"
      onClick={() => {
        logout({
          logoutParams: { returnTo: undefined },
          openUrl: false,
        });
      }}
    >
      <LogoutOutlined fontSize="small" />
    </IconButton>
  );

  if (isLoading) {
    return (
      <Box
        sx={{ height: "100vh", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <Typography sx={{ mr: 2 }}>Authenticating...</Typography>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (!isLoading && !isAuthenticated) {
    return (
      <Box
        sx={{
          height: "100vh",
          display: "grid",
          gridTemplateRows: "1fr 2fr 1fr",
          alignItems: "center",
          justifyItems: "center",
          padding: 2,
        }}
      >
        <Box component="img" src="assets/logo-jylo-combined.png" alt="Jylo Logo" sx={{width: 130}} />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography sx={{ fontWeight: "bold" }} align="center" variant="h4">
            Welcome to Jylo Office.
          </Typography>
          <Typography>Please sign in to continue.</Typography>
          <Button variant="contained" onClick={() => loginWithPopup()} sx={{ mt: 2 }}>
            Sign In
          </Button>
        </Box>
        <Box />
      </Box>
    );
  }

  if (isAuthenticated && isConfiguringApi) {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <LogoutButton />
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 2 }}>
          <Typography>Configuring API connection...</Typography>
          <CircularProgress size={24} />
        </Box>
      </Box>
    );
  }

  if (isAuthenticated && apiConfigError) {
    return (
      <Box sx={{ height: "100vh", display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <LogoutButton />
        </Box>
        <Box sx={{ flex: 1, display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: 2, p: 2 }}>
          <Alert severity="error" sx={{ maxWidth: 400 }}>
            <Typography variant="h6" gutterBottom>API Configuration Error</Typography>
            <Typography variant="body2" sx={{ mb: 2 }}>{apiConfigError}</Typography>
            <Button size="small" onClick={refreshConfiguration}>Retry</Button>
          </Alert>
        </Box>
      </Box>
    );
  }

  if (isAuthenticated && isApiConfigured) {
    return <>{children}</>;
  }

  return null;
};

export default AuthGate;
